import { LoggingPropertyConfiguration } from '../models/logging-property-configuration';
import { InitProperties } from '../models/init-properties';
import { ClientsiteBaseHandler } from './clientsite-base.handler';
import { Injectable, NgZone, inject } from '@angular/core';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { validateTrue } from '@rx-applications/shared/utils';
import { LoggingMessageInterface, LogChannel } from '@rx-applications/shared/logging';
import { DatadogTracker } from '@rx-applications/clientsite/shared/config/utils';
import { NoopDatadogRum } from './noop.handlers';
import { FeatureFlagEventBus } from '@rx-applications/shared/clientsite/feature-flags';
import { UserObj } from '../models/user-obj';

@Injectable({ providedIn: 'root' })
export class DatadogHandler extends ClientsiteBaseHandler {
  readonly #featureFlagEventBus = inject(FeatureFlagEventBus);
  protected readonly channelToSubscribe = LogChannel.Datadog;
  readonly #applicationName = 'Rx.ClientSite.UI';

  #ngZone = inject(NgZone);

  constructor() {
    super();
    this.#featureFlagEventBus.flagEvaluations$.subscribe(({ flag, value }) => {
      this.evaluateFeatureFlag(flag, value);
    });
  }

  get datadogRum() {
    return this.enabled ? datadogRum : NoopDatadogRum;
  }

  set user(user: UserObj) {
    this.datadogRum.setUser({
      name: user.name,
      email: user.email,
    });
  }

  set websiteId(id: number) {
    this.datadogRum.setGlobalContextProperty('usr.websiteId', id);
  }

  set isImpersonated(value: boolean) {
    this.datadogRum.setGlobalContextProperty('usr.isImpersonated', value);
  }

  set tenantId(value: string) {
    this.datadogRum.setGlobalContextProperty('tenantId', value);
  }

  set session(cid: string) {
    const currentContext = this.datadogRum.getGlobalContext();
    if (currentContext['cid'] === cid) {
      return;
    }
    const newContext = { ...currentContext, cid };
    this.datadogRum.setGlobalContext(newContext);
  }

  set configLoadTime(duration: number) {
    this.#trackConfigLoadTime(duration);
  }

  init(initProperties: InitProperties) {
    const tracker = initProperties?.environmentTracking?.['datadog'] as DatadogTracker;
    const { environment, appVersion } = initProperties;

    if (!tracker || !environment || !appVersion) {
      return;
    }

    if (this.subscription) {
      return; // already initialized
    }

    const { enabled, rumId, rumToken, key } = tracker;
    this.enabled = validateTrue(enabled);

    this.#ngZone.runOutsideAngular(() => {
      this.datadogRum.init({
        env: environment, // The application’s environment e.g. prod
        clientToken: rumToken,
        applicationId: rumId,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        service: this.#applicationName, // The service name for this application.
        version: appVersion, // The application’s version from Build Env
        trackUserInteractions: true, // Enables automatic collection of Users Actions
        trackResources: true, // Enables automatic collection of Resources (XHR, fetch)
        trackLongTasks: true,
        startSessionReplayRecordingManually: true,
        allowedTracingUrls: [/https:\/\/rxwebapi\.roomex\.(com|io|xyz|co)/, /https:\/\/staypi\.(?:dev\.|stg\.)?lodgx\.io/],
        defaultPrivacyLevel: 'allow',
        enableExperimentalFeatures: ['feature_flags'],
      });

      datadogLogs.init({
        env: environment,
        clientToken: key,
        site: 'datadoghq.com',
        forwardErrorsToLogs: true, // Set to false to stop forwarding console.error logs, uncaught exceptions and network errors
        sessionSampleRate: 100, // Percentage of sessions to track.
        service: this.#applicationName,
        version: appVersion,
      });
    });

    this.createSubscription();
  }

  createSetup(properties: LoggingPropertyConfiguration): void {
    if (properties.isImpersonated) {
      this.isImpersonated = properties.isImpersonated;
    }
    if (properties.username) {
      this.user = properties.username;
    }
    if (properties.websiteId) {
      this.websiteId = properties.websiteId;
    }
    if (properties.cid) {
      this.session = properties.cid;
    }
    if (properties.tenantId) {
      this.tenantId = properties.tenantId;
    }
    if (properties.configLoadTime) {
      this.#trackConfigLoadTime(properties.configLoadTime);
    }
    if (properties.recordSession) {
      this.#recordSession();
    }
  }

  protected override messageHandler = (value: LoggingMessageInterface) => {
    if (value?.type === 'action') {
      this.logCustomEvent(value?.name, value?.props);
    } else if (value?.type === 'error') {
      this.logCustomError(value.name, value.props);
    }
  };

  logCustomEvent(name: string, props = {}) {
    this.datadogRum.addAction(name, props);
  }

  logCustomError(details: string | Error, context = {}) {
    const error = typeof details === 'string' ? new Error(details) : details;
    this.datadogRum.addError(error, context);
  }

  /**
   * Each time a feature flag is evaluated,
   * add the following function to send the feature flag information to RUM:
   * [source Docs from Datadog](https://docs.datadoghq.com/real_user_monitoring/guide/setup-feature-flag-data-collection/?tab=browser#custom-feature-flag-management)
   *
   * @param featureFlagKey
   * @param value
   */

  evaluateFeatureFlag(featureFlagKey: string, value: boolean) {
    this.datadogRum.addFeatureFlagEvaluation(featureFlagKey, value);
  }

  #recordSession(): void {
    datadogRum.startSessionReplayRecording();
  }

  #trackConfigLoadTime(duration: number) {
    this.datadogRum.addAction('ConfigLoadTime', {
      duration,
    }); // datadog duration is in nanoseconds
  }
}
